import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { BookOpen, Sparkles, Download, Share2, Layout, FileText, ClipboardList, Sunrise, ChevronRight } from 'lucide-react';

const HERO_IMG = 'https://images.unsplash.com/photo-1580582932707-520aed937b7b?auto=format&fit=crop&w=1600&q=80';
const PHOTO_TEACH = 'https://images.unsplash.com/photo-1544717305-2782549b5136?auto=format&fit=crop&w=900&q=80';
const PHOTO_WORKSHEETS = 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=900&q=80';
const PHOTO_EXAM = 'https://images.unsplash.com/photo-1497633762265-9d179a990aa6?auto=format&fit=crop&w=900&q=80';
const PHOTO_CLASSROOM = 'https://images.unsplash.com/photo-1580582932707-520aed937b7b?auto=format&fit=crop&w=1600&q=80';
const PHOTO_MORNING = 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=1600&q=80';

const Landing = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 via-amber-50/60 to-white text-[#1C1917]">
      {/* HEADER */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-orange-200/40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shadow-lg">
              <Sunrise className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight">EduRepository</h1>
          </div>
          <div className="flex gap-2 flex-wrap justify-end">
            <Button data-testid="login-button" variant="ghost" onClick={() => navigate('/login')} className="text-[#1C1917] hover:text-orange-700 hover:bg-orange-50">Log In</Button>
            <Button data-testid="get-started-button" onClick={() => navigate('/login')} className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white rounded-full px-6 shadow-md hover:shadow-lg transition">Get Started</Button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative py-24 lg:py-32 px-6 overflow-hidden">
        {/* Warm sunrise gradient rays */}
        <div className="absolute inset-0 z-0 bg-[radial-gradient(circle_at_top_left,rgba(251,146,60,0.25),transparent_45%),radial-gradient(circle_at_bottom_right,rgba(253,224,71,0.28),transparent_50%)]" />
        {/* Soft image overlay */}
        <div className="absolute inset-0 z-0 opacity-25">
          <img src={PHOTO_MORNING} alt="Morning classroom light" className="w-full h-full object-cover" />
        </div>
        <div className="max-w-7xl mx-auto relative z-10 grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
          <div className="lg:col-span-3">
            <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur border border-orange-200 rounded-full px-4 py-1.5 mb-6 shadow-sm">
              <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" />
              <span className="text-xs font-semibold text-orange-700 tracking-widest uppercase">Fresh Morning · CBSE-Ready</span>
            </div>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight font-bold mb-6">
              Lesson Plans, Worksheets & <span className="bg-gradient-to-r from-orange-600 via-amber-500 to-orange-700 bg-clip-text text-transparent">CBSE Question Papers</span>
              <br />generated in minutes.
            </h2>
            <p className="text-lg text-stone-800 leading-relaxed mb-8 max-w-2xl">
              A warm, sunrise-orange workspace for teachers. Auto-generate NCFSE-aligned lesson plans, chapter-grounded worksheets, and full CBSE Sample Paper–pattern question papers — all downloadable as PDF & Word.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button data-testid="hero-cta-button" onClick={() => navigate('/login')} className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white rounded-full px-8 py-6 text-lg shadow-lg hover:shadow-xl transition">
                Start Planning <ChevronRight className="w-5 h-5 ml-1" />
              </Button>
              <Button data-testid="generate-worksheet-nav-button" onClick={() => navigate('/login')} variant="outline" className="border-2 border-orange-300 text-orange-700 hover:bg-orange-50 rounded-full px-8 py-6 text-lg">
                Generate a Question Paper
              </Button>
            </div>
            <div className="mt-8 flex items-center gap-6 text-sm text-stone-700">
              <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-orange-500" /> AI-powered</div>
              <div className="flex items-center gap-2"><Download className="w-4 h-4 text-orange-500" /> DOCX + PDF</div>
              <div className="flex items-center gap-2"><Share2 className="w-4 h-4 text-orange-500" /> Community library</div>
            </div>
          </div>

          <div className="lg:col-span-2 relative">
            <div className="absolute -inset-4 bg-gradient-to-br from-orange-400/30 to-amber-400/30 rounded-3xl blur-2xl" />
            <img src={HERO_IMG} alt="Bright classroom" className="relative rounded-3xl shadow-2xl ring-4 ring-white/60" />
            <div className="absolute -bottom-6 -left-6 bg-white/95 backdrop-blur rounded-2xl shadow-xl border border-orange-100 p-4 max-w-[220px]">
              <div className="flex items-center gap-2 mb-1"><FileText className="w-4 h-4 text-orange-600" /><span className="text-xs uppercase tracking-widest font-bold text-orange-700">CBSE SQP</span></div>
              <p className="text-sm font-semibold">80 marks · 5 sections · Case-study included</p>
            </div>
          </div>
        </div>
      </section>

      {/* THREE PILLARS */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs tracking-[0.2em] uppercase text-orange-600 font-bold text-center mb-3">Three tools · One workflow</p>
          <h3 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight text-center mb-14 font-bold">Everything a modern classroom needs.</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card data-testid="feature-lesson-plan" className="group overflow-hidden border border-orange-100 hover:-translate-y-2 transition-all duration-300 hover:shadow-2xl bg-white">
              <div className="h-40 relative overflow-hidden">
                <img src={PHOTO_TEACH} alt="Teacher planning" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-orange-600/80 via-orange-500/30 to-transparent" />
                <div className="absolute bottom-4 left-4 flex items-center gap-2">
                  <Sparkles className="w-6 h-6 text-white" />
                  <span className="text-white font-bold text-lg drop-shadow">Lesson Plans</span>
                </div>
              </div>
              <div className="p-6">
                <p className="text-base text-stone-800 leading-relaxed mb-4">
                  Auto-generate NCFSE 2023-aligned multi-day lesson plans in 7 languages. Just fill Class, Subject, Topic — AI writes Curriculum Goals, Outcomes, Skills, Innovative Practices & daily Exit Cards.
                </p>
                <ul className="text-sm text-stone-700 space-y-1.5">
                  <li>• 1–15 days, board & textbook aware</li>
                  <li>• Custom template columns supported</li>
                  <li>• Voice compose with mic</li>
                </ul>
              </div>
            </Card>

            <Card data-testid="feature-worksheet" className="group overflow-hidden border border-orange-100 hover:-translate-y-2 transition-all duration-300 hover:shadow-2xl bg-white">
              <div className="h-40 relative overflow-hidden">
                <img src={PHOTO_WORKSHEETS} alt="Student worksheet" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-amber-600/80 via-amber-500/30 to-transparent" />
                <div className="absolute bottom-4 left-4 flex items-center gap-2">
                  <ClipboardList className="w-6 h-6 text-white" />
                  <span className="text-white font-bold text-lg drop-shadow">Worksheets</span>
                </div>
              </div>
              <div className="p-6">
                <p className="text-base text-stone-800 leading-relaxed mb-4">
                  Chapter-grounded worksheets with 13 question types. Upload a PDF or photos — AI writes questions strictly from that content, with answer keys.
                </p>
                <ul className="text-sm text-stone-700 space-y-1.5">
                  <li>• MCQ, Assertion & Reason, Passages…</li>
                  <li>• Set counts per type, auto-scale marks</li>
                  <li>• Photo & PDF grounding via vision AI</li>
                </ul>
              </div>
            </Card>

            <Card data-testid="feature-question-paper" className="group overflow-hidden border-2 border-orange-300 hover:-translate-y-2 transition-all duration-300 hover:shadow-2xl bg-gradient-to-b from-orange-50 to-white relative">
              <div className="absolute -top-3 right-4 z-10 bg-gradient-to-r from-orange-500 to-amber-500 text-white text-[10px] font-bold px-3 py-1 rounded-full shadow-md tracking-widest">NEW</div>
              <div className="h-40 relative overflow-hidden">
                <img src={PHOTO_EXAM} alt="Question paper" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-orange-700/80 via-orange-500/30 to-transparent" />
                <div className="absolute bottom-4 left-4 flex items-center gap-2">
                  <FileText className="w-6 h-6 text-white" />
                  <span className="text-white font-bold text-lg drop-shadow">Question Papers</span>
                </div>
              </div>
              <div className="p-6">
                <p className="text-base text-stone-800 leading-relaxed mb-4">
                  Full CBSE Sample Question Paper-pattern generation. Choose Class 3–12, pick a pattern, and get a print-ready paper with sections, case studies, and marking scheme.
                </p>
                <ul className="text-sm text-stone-700 space-y-1.5">
                  <li>• CBSE SQP 2024-25 patterns preloaded</li>
                  <li>• MCQ · Short · Long · Case-study</li>
                  <li>• DOCX & PDF download</li>
                </ul>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* PHOTO STRIP */}
      <section className="py-20 px-6 bg-gradient-to-r from-orange-100 via-amber-50 to-yellow-50 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs tracking-[0.2em] uppercase text-orange-600 font-bold mb-2">A day in class</p>
            <h3 className="text-3xl sm:text-4xl font-bold">Built with teachers in mind.</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[PHOTO_CLASSROOM, PHOTO_WORKSHEETS, PHOTO_TEACH, PHOTO_EXAM].map((src, i) => (
              <div key={i} className="relative group overflow-hidden rounded-2xl shadow-lg">
                <img src={src} alt="" className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-orange-600/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SUPPORTING FEATURES */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-6">
          {[
            { icon: Sparkles, title: 'AI Suggestions', body: 'NCFSE 2023 competency codes & curriculum goals built-in.' },
            { icon: Layout, title: 'Template Library', body: "Upload your school's format — AI fills every column exactly." },
            { icon: Download, title: 'Bilingual Export', body: 'Times New Roman 14pt with Devanagari fallback for Hindi.' },
            { icon: Share2, title: 'Community Library', body: 'Anonymized library for plans, worksheets & question papers.' },
          ].map(({ icon: I, title, body }) => (
            <Card key={title} className="p-6 border border-orange-100 hover:shadow-lg transition group">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center mb-4 shadow-md group-hover:scale-110 transition">
                <I className="w-6 h-6 text-white" />
              </div>
              <h5 className="font-bold text-lg mb-1">{title}</h5>
              <p className="text-sm text-stone-700">{body}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 bg-gradient-to-br from-orange-500 via-amber-500 to-yellow-500 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_20%_20%,white,transparent_35%),radial-gradient(circle_at_80%_80%,white,transparent_40%)]" />
        <div className="max-w-4xl mx-auto text-center relative z-10 text-white">
          <h3 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-6 font-bold">Start your fresh morning.</h3>
          <p className="text-lg mb-8 text-white/95 leading-relaxed max-w-2xl mx-auto">
            Join teachers across India crafting professional lesson plans, worksheets and CBSE-pattern question papers — faster than ever.
          </p>
          <Button data-testid="footer-cta-button" onClick={() => navigate('/login')} className="bg-white text-orange-700 hover:bg-orange-50 rounded-full px-10 py-6 text-lg font-bold shadow-2xl">
            Get Started Free
          </Button>
        </div>
      </section>

      <footer className="py-8 px-6 border-t border-orange-100 bg-white">
        <div className="max-w-7xl mx-auto text-center text-sm text-stone-500">
          © 2026 EduRepository · Reserve ID Education Solution
        </div>
      </footer>
    </div>
  );
};

export default Landing;
